/**
 * Claude-Code-style intermediate steps timeline rendered above an assistant
 * message. Shows tool calls, retrievals, and other agent activity live as
 * they stream in, then collapses once the message is done.
 *
 * Implements CHAT_INTERMEDIATE_STEPS_SPEC §4 (layout, tokens, typography,
 * icons, interactions, mobile).
 *
 * Presentational only — reads StepRecord[] from props; all state mutation
 * happens in chatSlice.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { StepRecord, StepKind, StepStatus } from '../../store/slices/chatSlice';

export interface MessageStepsProps {
  steps?: StepRecord[] | null;
  /**
   * When true, the component never auto-collapses — used for old messages
   * restored from the server. Defaults to false so live-streaming messages
   * stay expanded; the user clicks to collapse.
   */
  static_?: boolean;
}

// Collapse animation duration — manually tuned (MUI's "auto" measured the
// scroll height every frame which caused a visible stutter when steps were
// appended during streaming). 220ms feels deliberate without feeling slow.
const COLLAPSE_TRANSITION_MS = 220;

const kindIcon = (kind: StepKind, status: StepStatus, sizeOverride?: number) => {
  const size = sizeOverride ?? 16;
  const commonSx = { fontSize: size };
  // Running state: always a spinner, regardless of kind, so the UX reads
  // the same way as Claude/assistant-ui.
  if (status === 'running') {
    return <CircularProgress size={size - 2} thickness={5} />;
  }
  if (status === 'error') {
    return <ErrorOutlineIcon sx={{ ...commonSx, color: 'error.main' }} />;
  }
  // Done — kind-specific
  switch (kind) {
    case 'retrieval':
      return <StorageOutlinedIcon sx={{ ...commonSx, color: 'success.main' }} />;
    case 'web_search':
      return <LanguageOutlinedIcon sx={{ ...commonSx, color: 'success.main' }} />;
    case 'thinking':
      return <PsychologyOutlinedIcon sx={{ ...commonSx, color: 'success.main' }} />;
    case 'tool_call':
      return <CheckCircleOutlineIcon sx={{ ...commonSx, color: 'success.main' }} />;
    default:
      return <BuildOutlinedIcon sx={{ ...commonSx, color: 'success.main' }} />;
  }
};

const formatDuration = (ms?: number | null): string => {
  if (ms == null || ms < 0) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const humanizeLabel = (label?: string | null): string =>
  // search_agrana_emails -> "Search agrana emails"
  (label || 'Step')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

const MessageSteps: React.FC<MessageStepsProps> = ({ steps, static_ = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Normalized, memoized derivations.
  const { hasSteps, anyRunning, allDone, totalMs } = useMemo(() => {
    const safe = Array.isArray(steps) ? steps : [];
    const running = safe.some((s) => s.status === 'running');
    const done = safe.length > 0 && !running;
    const total = safe.reduce((acc, s) => acc + (s.duration_ms ?? 0), 0);
    return {
      hasSteps: safe.length > 0,
      anyRunning: running,
      allDone: done,
      totalMs: total,
    };
  }, [steps]);

  // Stay expanded while the message is live (streaming or just completed).
  // Auto-collapse was removed because it caused the assistant bubble below
  // the timeline to jump upward 1.5s after completion — the #1 source of the
  // "not smooth" feedback. Users still click the header to collapse at will;
  // restored sessions render with static_=true and start collapsed.
  const [expanded, setExpanded] = useState<boolean>(!static_);

  useEffect(() => {
    if (static_) {
      setExpanded(false);
      return;
    }
    if (anyRunning) {
      setExpanded(true);
    }
  }, [anyRunning, static_]);

  if (!hasSteps) return null;
  const safeSteps = steps as StepRecord[];

  const headerLabel = anyRunning
    ? 'Thinking'
    : `Reasoning · ${safeSteps.length} step${safeSteps.length === 1 ? '' : 's'}${
        totalMs ? ` · ${formatDuration(totalMs)}` : ''
      }`;

  return (
    <Paper
      variant="outlined"
      elevation={0}
      sx={{
        mb: 1.5,
        p: isMobile ? '8px 10px' : '10px 14px',
        borderRadius: '10px',
        borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'),
        maxWidth: '100%',
      }}
    >
      {/* Header — click to toggle */}
      <Box
        onClick={() => setExpanded((e) => !e)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
          minHeight: 24,
          // Isolate hover / click repaints to this subtree so the bubble
          // below doesn't re-layout when the user scrubs over the header.
          contain: 'layout style',
        }}
        role="button"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse reasoning steps' : 'Expand reasoning steps'}
      >
        <Box sx={{ width: 18, display: 'flex', justifyContent: 'center' }}>
          {anyRunning ? (
            <CircularProgress size={14} thickness={5} disableShrink />
          ) : safeSteps.some((s) => s.status === 'error') ? (
            <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
          ) : (
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            fontSize: 13,
            lineHeight: 1.4,
            color: 'text.primary',
            flex: 1,
          }}
        >
          {headerLabel}
        </Typography>
        <IconButton
          size="small"
          tabIndex={-1}
          sx={{
            p: 0.25,
            color: 'text.secondary',
            // Smooth chevron rotation instead of a two-frame icon swap.
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 180ms ease-out',
          }}
          aria-hidden
          onClick={(e) => e.stopPropagation()}
        >
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Steps list */}
      <Collapse in={expanded} timeout={COLLAPSE_TRANSITION_MS} unmountOnExit>
        <Box
          sx={{
            mt: 1,
            maxHeight: 300,
            overflowY: 'auto',
            // divider between steps via sibling rule
            '& > .step-row + .step-row': {
              borderTop: '1px solid',
              borderColor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              pt: 1,
              mt: 1,
            },
          }}
        >
          {safeSteps.map((step) => (
            <StepRow key={step.id} step={step} isMobile={isMobile} />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

interface StepRowProps {
  step: StepRecord;
  isMobile: boolean;
}

// Custom equality: only re-render when the fields this row actually renders
// change. Prevents a running step's timer tick (or any unrelated parent
// state change) from invalidating N completed rows every frame — which was
// causing the CircularProgress spinner stutter and perceived "clunkiness"
// when a new step.start landed mid-stream.
const stepRowPropsEqual = (a: StepRowProps, b: StepRowProps) =>
  a.isMobile === b.isMobile &&
  a.step.id === b.step.id &&
  a.step.status === b.step.status &&
  a.step.duration_ms === b.step.duration_ms &&
  a.step.label === b.step.label &&
  a.step.detail === b.step.detail &&
  a.step.result_preview === b.step.result_preview &&
  a.step.error_message === b.step.error_message;

const StepRowInner: React.FC<StepRowProps> = ({ step, isMobile }) => {
  const durationLabel =
    step.status === 'running'
      ? 'running…'
      : formatDuration(step.duration_ms);

  return (
    <Box
      className="step-row"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        flexDirection: isMobile ? 'column' : 'row',
        px: 0.5,
        py: 0.25,
        borderRadius: 1,
        '&:hover': {
          bgcolor: (t) =>
            t.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.03)'
              : 'rgba(0,0,0,0.025)',
        },
      }}
    >
      <Box
        sx={{
          width: isMobile ? 18 : 20,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pt: '2px',
          flexShrink: 0,
        }}
      >
        {kindIcon(step.kind, step.status, isMobile ? 14 : 16)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.35,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {humanizeLabel(step.label)}
        </Typography>
        {step.detail && (
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 400,
              lineHeight: 1.4,
              color: 'text.secondary',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              mt: 0.25,
              whiteSpace: 'pre-wrap',
            }}
          >
            {step.detail}
          </Typography>
        )}
        {(step.result_preview || step.error_message) && (
          <Box
            sx={{
              mt: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: (t) =>
                t.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.04)'
                  : 'rgba(0,0,0,0.04)',
              fontFamily: 'Menlo, Consolas, monospace',
              fontSize: 11,
              lineHeight: 1.5,
              color: step.error_message ? 'error.main' : 'text.primary',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: isMobile ? 1 : 2,
              WebkitBoxOrient: 'vertical',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {step.error_message || step.result_preview}
          </Box>
        )}
      </Box>
      {durationLabel && (
        <Tooltip title={step.status === 'running' ? 'in progress' : `${step.duration_ms ?? ''} ms`}>
          <Typography
            sx={{
              fontSize: 11,
              color: step.status === 'running' ? 'primary.main' : 'text.secondary',
              fontWeight: step.status === 'running' ? 500 : 400,
              lineHeight: 1.4,
              flexShrink: 0,
              alignSelf: isMobile ? 'flex-start' : 'center',
              pl: isMobile ? 3.5 : 0,
            }}
          >
            {durationLabel}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

const StepRow = React.memo(StepRowInner, stepRowPropsEqual);

export default React.memo(MessageSteps);
